import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Bell, Calendar, MapPin, Home, AlertCircle, Loader2, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { API } from '@/App';
import { toast } from 'sonner';

const AlertsHistoryPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [requestingLink, setRequestingLink] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAlerts();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/newsletter/alerts-history?token=${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const requestAccessLink = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      toast.error('Por favor ingresa tu email');
      return;
    }
    
    setRequestingLink(true);
    try {
      await axios.post(`${API}/newsletter/resend-access-link?email=${encodeURIComponent(emailInput)}`);
      setLinkSent(true);
      toast.success('Si el email está suscrito, recibirás el enlace');
    } catch (err) {
      toast.error('Error al enviar el enlace');
    } finally {
      setRequestingLink(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  // No token provided - show request form
  if (!token) {
    return (
      <>
        <SEO 
          title="Mi Historial de Alertas | Ikonic Deals"
          description="Accede a tu historial de alertas de propiedades"
          noindex={true}
        />
        <Header />
        
        <section className="pt-32 pb-24 min-h-screen">
          <div className="container-custom px-4 sm:px-6 max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <Bell size={64} className="mx-auto text-[#D4AF37] mb-6" />
              <h1 className="text-2xl text-white mb-4">
                {t('alerts.accessHistory', 'Accede a tu historial')}
              </h1>
              <p className="text-[#A3A3A3] mb-8">
                {t('alerts.enterEmail', 'Ingresa tu email para recibir un enlace de acceso a tu historial de alertas.')}
              </p>
              
              {linkSent ? (
                <div className="bg-[#121212] border border-[#D4AF37]/30 p-6 rounded-lg">
                  <CheckCircle size={48} className="mx-auto text-[#D4AF37] mb-4" />
                  <p className="text-white">
                    {t('alerts.linkSent', '¡Listo! Si tu email está suscrito, recibirás el enlace en tu bandeja de entrada.')}
                  </p>
                </div>
              ) : (
                <form onSubmit={requestAccessLink} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                    <Input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="pl-10 bg-[#121212] border-white/10 text-white"
                      placeholder="tu@email.com"
                      required
                      data-testid="alerts-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={requestingLink}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                    data-testid="request-alerts-link-btn"
                  >
                    {requestingLink ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar enlace de acceso'
                    )}
                  </Button>
                </form>
              )}
              
              <p className="text-[#525252] text-sm mt-8">
                {t('alerts.notSubscribed', '¿Aún no estás suscrito?')}{' '}
                <Link to="/propiedades" className="text-[#D4AF37] hover:underline">
                  {t('alerts.subscribeNow', 'Suscríbete a nuestras alertas')}
                </Link>
              </p>
            </motion.div>
          </div>
        </section>

        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <section className="pt-32 pb-24 min-h-screen">
          <div className="container-custom px-4 sm:px-6 text-center">
            <Loader2 size={48} className="mx-auto text-[#D4AF37] animate-spin" />
            <p className="text-[#A3A3A3] mt-4">Cargando historial...</p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <section className="pt-32 pb-24 min-h-screen">
          <div className="container-custom px-4 sm:px-6 max-w-md mx-auto text-center">
            <AlertCircle size={64} className="mx-auto text-red-500 mb-6" />
            <h1 className="text-2xl text-white mb-4">Error</h1>
            <p className="text-[#A3A3A3] mb-8">{error}</p>
            <Link to="/">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                Volver al inicio
              </Button>
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Mi Historial de Alertas | Ikonic Deals"
        description="Tu historial personal de alertas de propiedades"
        noindex={true}
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              {t('alerts.subtitle', 'Tu Perfil')}
            </span>
            <h1 className="mt-3 text-3xl md:text-4xl text-white flex items-center gap-3">
              <Bell className="text-[#D4AF37]" size={32} />
              {t('alerts.title', 'Mi Historial de Alertas')}
            </h1>
            <p className="mt-2 text-[#A3A3A3]">
              {data?.subscriber?.email}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subscription Info */}
      <section className="py-6 border-b border-white/5">
        <div className="container-custom px-4 sm:px-6">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-[#A3A3A3]">Suscrito desde:</span>
              <span className="text-white ml-2">
                {formatDate(data?.subscriber?.subscribed_at)}
              </span>
            </div>
            <div>
              <span className="text-[#A3A3A3]">Estado:</span>
              <span className={`ml-2 ${data?.subscriber?.is_active ? 'text-green-500' : 'text-red-500'}`}>
                {data?.subscriber?.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            <div>
              <span className="text-[#A3A3A3]">Alertas recibidas:</span>
              <span className="text-[#D4AF37] ml-2 font-mono">{data?.total || 0}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Alerts List */}
      <section className="py-12 min-h-[50vh]">
        <div className="container-custom px-4 sm:px-6">
          {data?.alerts?.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <Bell size={64} className="mx-auto text-white/20 mb-6" />
              <h2 className="text-2xl text-white mb-4">
                {t('alerts.noAlerts', 'Sin alertas todavía')}
              </h2>
              <p className="text-[#A3A3A3] mb-8 max-w-md mx-auto">
                {t('alerts.noAlertsDescription', 'Cuando publiquemos propiedades que coincidan con tus preferencias, te enviaremos una alerta y aparecerá aquí.')}
              </p>
              <Link to="/propiedades">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                  {t('alerts.exploreProperties', 'Explorar Propiedades')}
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-4" data-testid="alerts-list">
              {data?.alerts?.map((alert, index) => (
                <motion.div
                  key={alert.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#121212] border border-white/5 overflow-hidden hover:border-[#D4AF37]/30 transition-colors"
                  data-testid={`alert-item-${index}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    {alert.image_url && (
                      <div className="md:w-48 h-32 md:h-auto flex-shrink-0">
                        <img
                          src={alert.image_url}
                          alt={alert.property_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                        <div>
                          <h3 className="text-white font-medium">
                            {alert.property_name}
                          </h3>
                          <p className="text-[#A3A3A3] text-sm flex items-center gap-1 mt-1">
                            <MapPin size={14} className="text-[#D4AF37]" />
                            {alert.location}
                          </p>
                        </div>
                        <p className="text-[#D4AF37] font-mono text-lg">
                          {formatPrice(alert.price_usd)}
                        </p>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                        <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1">
                          {alert.property_type}
                        </span>
                        <span className="text-[#A3A3A3]">
                          {alert.transaction_type}
                        </span>
                        <span className="text-[#525252] flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(alert.sent_at)}
                        </span>
                      </div>
                      
                      {alert.property_url && (
                        <div className="mt-4">
                          <a
                            href={alert.property_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] text-sm hover:underline inline-flex items-center gap-1"
                          >
                            <Home size={14} />
                            Ver propiedad
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AlertsHistoryPage;
